// src/features/ask/AssistantProvider.tsx
//
// «Preguntar» state that outlives the screen: whether the side panel is open
// and the conversation, so a director can navigate while it stays put. Each
// question carries the open screen (useCurrentView); an answer that asks to
// change the screen's filters is applied at once and keeps a snapshot, so the
// most recent change can be undone from its own turn.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { getCurrentRole } from '@/utils/auth';
import type { AskResponse, AskStatus, AskTurn as AskHistoryTurn, AskView } from '@/types/assistant';
import { useCurrentView, type ViewSnapshot } from './currentView';
import { isDocumentationOnly } from '@/features/developers/access';

export type AskTurnState =
  | { status: 'pending' }
  | { status: 'done'; response: AskResponse; applied?: { snapshot: ViewSnapshot; undone: boolean } }
  | { status: 'error'; messageKey: string };

export interface AskTurn {
  id: number;
  question: string;
  state: AskTurnState;
}

interface AssistantContextValue {
  enabled: boolean;
  /** The screen the next question will be asked about. */
  view: AskView | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  turns: AskTurn[];
  busy: boolean;
  hotelsInScope: number | null;
  send: (text: string, retryId?: number) => void;
  undo: (turnId: number) => void;
  /** The turn whose screen change can still be undone (the most recent one). */
  undoableTurnId: number | null;
  startOver: () => void;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

const errorMessageKey = (error: unknown): string => {
  const status = (error as { status?: number } | null)?.status;
  if (status === 503) return 'resources.ask.errors.unavailable';
  if (status === 429) return 'resources.ask.errors.rateLimited';
  if (status === 403) return 'resources.ask.errors.forbidden';
  if (status === 400) return 'resources.ask.errors.invalid';
  return 'resources.ask.errors.generic';
};

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dataProvider = useDataProvider();
  const { view, apply, restore } = useCurrentView();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<AskTurn[]>([]);
  const nextId = useRef(1);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const role = getCurrentRole();
  const status = useQuery<AskStatus>({
    queryKey: ['insight-ask', 'status', role],
    queryFn: async ({ signal }) => (await (dataProvider as any).getAskStatus(signal)).data,
    staleTime: 5 * 60_000,
    enabled: Boolean(role) && !isDocumentationOnly(),
  });
  const enabled = status.data?.enabled === true;

  const busy = turns.some((turn) => turn.state.status === 'pending');
  const lastDone = [...turns].reverse().find((turn) => turn.state.status === 'done');
  const hotelsInScope = lastDone?.state.status === 'done' ? lastDone.state.response.hotelsInScope : null;
  const undoable = [...turns].reverse().find((turn) => turn.state.status === 'done' && turn.state.applied);
  const undoableTurnId = undoable && undoable.state.status === 'done' && !undoable.state.applied?.undone ? undoable.id : null;

  // Read the view and apply actions through refs, so `send` does not change on every filter change.
  const viewRef = useRef(view);
  viewRef.current = view;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const turnsRef = useRef(turns);
  turnsRef.current = turns;

  const send = useCallback(
    async (text: string, retryId?: number) => {
      const question = text.trim();
      if (!question) return;
      const history: AskHistoryTurn[] = turnsRef.current
        .filter((turn) => turn.id !== retryId && turn.state.status === 'done')
        .map((turn) => ({ question: turn.question, answer: (turn.state as { response: AskResponse }).response.answer }));
      const id = retryId ?? nextId.current++;
      const pending: AskTurn = { id, question, state: { status: 'pending' } };
      setTurns((current) => (retryId ? current.map((turn) => (turn.id === retryId ? pending : turn)) : [...current, pending]));

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const { data } = await (dataProvider as any).ask({ question, history, view: viewRef.current }, controller.signal);
        const response = data as AskResponse;
        const applied = response.viewAction ? { snapshot: applyRef.current(response.viewAction), undone: false } : undefined;
        setTurns((current) => current.map((turn) => (turn.id === id ? { ...turn, state: { status: 'done', response, applied } } : turn)));
      } catch (error) {
        if (controller.signal.aborted) return;
        setTurns((current) => current.map((turn) => (turn.id === id ? { ...turn, state: { status: 'error', messageKey: errorMessageKey(error) } } : turn)));
      }
    },
    [dataProvider]
  );

  const undo = useCallback(
    (turnId: number) => {
      const turn = turns.find((t) => t.id === turnId);
      if (!turn || turn.state.status !== 'done' || !turn.state.applied || turn.state.applied.undone) return;
      restore(turn.state.applied.snapshot);
      setTurns((current) =>
        current.map((t) =>
          t.id === turnId && t.state.status === 'done' && t.state.applied
            ? { ...t, state: { ...t.state, applied: { ...t.state.applied, undone: true } } }
            : t
        )
      );
    },
    [turns, restore]
  );

  const startOver = useCallback(() => {
    abortRef.current?.abort();
    setTurns([]);
  }, []);

  const value = useMemo<AssistantContextValue>(
    () => ({ enabled, view, open: open && enabled, setOpen, turns, busy, hotelsInScope, send, undo, undoableTurnId, startOver }),
    [enabled, view, open, turns, busy, hotelsInScope, send, undo, undoableTurnId, startOver]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

export const useAssistant = (): AssistantContextValue => {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within an AssistantProvider');
  return ctx;
};
